<script setup lang="ts">
import { computed, type HTMLAttributes, inject, onUnmounted, provide, watch } from 'vue';
import { tabsInjectionKey } from './internal';
import { tabIdInjectionKey } from './tabId';

const props = defineProps<
  {
    /** A unique identifier of the tabpanel within the `NxTabs` component. Only used internally */
    tabId: string;
    /** The label for the tabpanel. Only shown on a generated `NxTab` component if wrapped within a `NxTabs` component */
    tabLabel: string;
    /** Whether the content of this tabpanel should be loaded lazy. Defaults to eager loading (`false`) */
    loadLazy?: boolean;
    /** Optional counter shown beside the tab label (e.g. open invitations). Hidden when unset */
    tabCount?: number;
    /** Let the panel's content own scrolling inside a bounded flex column. */
    fill?: boolean;
  } & /* @vue-ignore */ HTMLAttributes
>();

const tabsInjection = inject(tabsInjectionKey);
const removeTab = tabsInjection?.addTab(props.tabId, props.tabLabel, props.tabCount);
// Labels and counts may change after mount (translated labels, live counters).
watch(
  () => [props.tabLabel, props.tabCount] as const,
  ([label, count]) => tabsInjection?.updateTab(props.tabId, label, count)
);

// Expose this panel's tabId to descendants (section components read it via useTabId()).
provide(
  tabIdInjectionKey,
  computed(() => props.tabId)
);
const hidden = computed(() => tabsInjection?.selectedTabId.value !== props.tabId);

onUnmounted(() => {
  removeTab?.();
});
</script>
<template>
  <div
    :id="tabsInjection?.getTabpanelElementId($props.tabId)"
    role="tabpanel"
    tabindex="-1"
    :aria-labelledby="tabsInjection?.getTabElementId($props.tabId)"
    :hidden="hidden"
    :class="{
      'is-spaced': tabsInjection?.spaced.value,
      'is-fill': tabsInjection?.fill.value,
      'is-flow': tabsInjection?.flow.value,
      'is-content-fill': props.fill,
    }"
  >
    <slot v-if="!$props.loadLazy || !hidden"></slot>
  </div>
</template>
<style scoped>
[role='tabpanel'] {
  /* `flex: 1 1 auto` lets the panel both grow into and shrink below its content size when
     inside a constrained flex column (so the panel itself scrolls), while sizing to content
     when the parent has no fixed height (e.g. demo/showcase). */
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  overflow-y: auto;
  outline: none;
}

/* `NxTabs spaced`: the content does not sit on the tab row. */
[role='tabpanel'].is-spaced {
  padding-block-start: var(--nxt-space-md);
}

/* `fill` on NxTabs: the panel scrolls below the standing tablist, one step
   apart from it, and keeps its scroll from chaining to the page. */
[role='tabpanel'].is-fill {
  padding-block-start: var(--nxt-space-md);
  box-sizing: border-box;
  overscroll-behavior: contain;
}

/* `flow` on NxTabs: the panel is no scroll container; the surrounding container scrolls. */
[role='tabpanel'].is-flow {
  overflow: visible;
}

/* Do not override the native hidden state of inactive panels. */
[role='tabpanel'].is-content-fill:not([hidden]) {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
</style>
